/**
 * WeeklyGoalsCard (iter1066) — „Моите цели за седмицата".
 * Обща за целия организъм: целите идват от разговора с мрежата, от страница
 * на близнак или се пишат на ръка.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Check, Plus, Target, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function WeeklyGoalsCard({ compact = false }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/weekly-goals`).then(({ data }) => setData(data)).catch(() => {});
  }, [API]);

  useEffect(() => {
    load();
    const onSaved = () => load();
    window.addEventListener("weekly-goal-saved", onSaved);
    return () => window.removeEventListener("weekly-goal-saved", onSaved);
  }, [load]);

  const add = async () => {
    const t = text.trim();
    if (!t) return;
    try {
      await axios.post(`${API}/weekly-goals`, { text: t, source: "ръчно" });
      setText("");
      setAdding(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Целта не се записа.");
    }
  };

  const toggle = async (g) => {
    try {
      await axios.patch(`${API}/weekly-goals/${g.goal_id}`, { done: !g.done });
      load();
    } catch { toast.error("Промяната не мина."); }
  };

  const remove = async (g) => {
    try {
      await axios.delete(`${API}/weekly-goals/${g.goal_id}`);
      load();
    } catch { toast.error("Изтриването не мина."); }
  };

  if (!data) return null;
  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid="weekly-goals-card">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold inline-flex items-center gap-2">
          <Target className="w-4 h-4 text-emerald-500" /> {data.title_bg}
        </h3>
        <span className="text-[11px] opacity-60" data-testid="weekly-goals-progress">
          {data.done} от {data.total}
        </span>
      </div>

      {data.goals.length === 0 && (
        <p className="text-xs opacity-60 mt-2" data-testid="weekly-goals-empty">{data.empty_bg}</p>
      )}

      <div className="mt-2.5 space-y-1.5">
        {data.goals.map((g) => (
          <div key={g.goal_id}
            className={`flex items-start gap-2 rounded-xl px-2.5 py-2 ${
              isDark ? "bg-slate-800/50" : "bg-slate-50"}`}
            data-testid={`weekly-goal-${g.goal_id}`}>
            <button type="button" onClick={() => toggle(g)}
              data-testid={`weekly-goal-toggle-${g.goal_id}`}
              className={`mt-0.5 w-4 h-4 rounded-md border shrink-0 flex items-center justify-center ${
                g.done
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : isDark ? "border-slate-600" : "border-slate-300"}`}>
              {g.done && <Check className="w-3 h-3" />}
            </button>
            <div className="min-w-0 flex-1">
              <p className={`text-xs leading-snug ${g.done ? "line-through opacity-50" : ""}`}>
                {g.text}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                {g.twin_label_bg && (
                  <button type="button" onClick={() => g.twin_route && navigate(g.twin_route)}
                    className="text-[10px] opacity-60 hover:opacity-100 underline"
                    data-testid={`weekly-goal-twin-${g.goal_id}`}>
                    {g.twin_label_bg}
                  </button>
                )}
                {g.source && <span className="text-[10px] opacity-40">от: {g.source}</span>}
              </div>
            </div>
            <button type="button" onClick={() => remove(g)}
              data-testid={`weekly-goal-delete-${g.goal_id}`}
              className="opacity-40 hover:opacity-100 shrink-0">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {!compact && (
        adding ? (
          <div className="mt-2.5 flex items-center gap-2">
            <input
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="Например: три разходки по 30 минути"
              data-testid="weekly-goals-input"
              className={`flex-1 rounded-xl px-3 py-1.5 text-xs outline-none border ${
                isDark ? "bg-slate-950/60 border-slate-700" : "bg-white border-slate-200"}`}
            />
            <Button size="sm" onClick={add} data-testid="weekly-goals-save-btn">Запишете</Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" className="mt-2.5"
            disabled={data.total >= data.max_goals}
            onClick={() => setAdding(true)} data-testid="weekly-goals-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Нова цел
          </Button>
        )
      )}
    </div>
  );
}
